import FileSelector from 'components/Grading/FileSelector';
import LegacyFeedbackRenderer from 'components/Grading/LegacyFeedbackRenderer';
import FeedbackRenderer from 'components/InstructorFeedback/FeedbackRenderer';
import Header from 'components/InstructorFeedback/Header';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Container,
    Dropdown,
    IconButton,
    PDFViewer,
    RichTextArea,
    SheetViewer,
    Stack,
    styled,
    Text,
} from 'shell/components';
import theme from 'shell/themes';
import { getS3FileURL } from 'utils/helper';

const Desktop = ({
    form,
    setForm,
    selectedFeedbackId,
    setSelectedFeedbackId,
    isDocumentFullScreen,
    setIsDocumentFullScreen,
    currentFeedbackItem,
    feedbackItems,
    currentFeedbackItemIndex,
    highlights,
    onClickPrevAttempt,
    onClickNextAttempt,
    onSelect,
    updateFeedback,
}) => {
    const { t } = useTranslation('assignment');

    const url = useMemo(() => getS3FileURL(currentFeedbackItem.submission), [currentFeedbackItem?.submission]);

    const renderableHighlights = highlights.map(({ id, value }) => {
        if (typeof value === 'string') return { id, value };
        return { id: id, value: value.highlight };
    });

    return (
        <RootContainer>
            <Header />
            <Stack sx={isDocumentFullScreen ? { overflow: 'hidden' } : undefined} direction={'row'}>
                <LeftStack isDocumentFullScreen={isDocumentFullScreen}>
                    {isDocumentFullScreen && typeof currentFeedbackItem.submission !== 'string' && (
                        <BannerBox>
                            <Text color={'inherit'}>
                                {t(
                                    'grading-section.document-preview.feedback-annotations',
                                    'Currently viewing annotations for feedback.'
                                )}
                            </Text>
                        </BannerBox>
                    )}
                    {typeof currentFeedbackItem.submission === 'string' ? (
                        <Stack sx={{ height: '100%', p: 3 }}>
                            <CustomRichTextArea readOnly value={currentFeedbackItem.submission} onChange={null} />
                        </Stack>
                    ) : currentFeedbackItem.submission.type === 'application/pdf' ? (
                        <PDFViewer
                            highlights={renderableHighlights}
                            isFullScreen={isDocumentFullScreen}
                            url={url}
                            inlineComments={currentFeedbackItem.inline_comments}
                            selectedInlineCommentId={null}
                            setInlineComments={() => {
                                return null;
                            }}
                            setSelectedInlineCommentId={() => {
                                return null;
                            }}
                            setIsFullScreen={setIsDocumentFullScreen}
                            isStudent={true}
                        />
                    ) : (
                        <SheetViewer
                            highlights={renderableHighlights}
                            url={getS3FileURL(currentFeedbackItem.submission)}
                        />
                    )}
                    <FileSelector
                        submission={currentFeedbackItem}
                        selectedFileId={currentFeedbackItem.files[0].id}
                        onClick={() => {}}
                    />
                </LeftStack>
                <RightStack isDocumentFullScreen={isDocumentFullScreen}>
                    <Stack sx={{ py: 2, px: 3 }} direction={'row'} justifyContent={'space-between'}>
                        <Text
                            component={'h1'}
                            noWrap
                            title={currentFeedbackItem.submission?.name}
                            variant={'sh2'}
                            weight={'semibold'}
                        >
                            {currentFeedbackItem.submission?.name}
                        </Text>
                        <Stack sx={{ flexShrink: 0 }} alignItems={'center'} direction={'row'} spacing={4}>
                            <Stack alignItems={'center'} direction={'row'} spacing={1}>
                                <IconButton
                                    aria-label={'view previous attempt'}
                                    disabled={!currentFeedbackItemIndex}
                                    kind={'chevronLeft'}
                                    size={'small'}
                                    variant={'square'}
                                    onClick={onClickPrevAttempt}
                                />
                                <Text>
                                    {t('attempt-status', 'Attempt {{attemptStatus}}', {
                                        attemptStatus: `${currentFeedbackItemIndex + 1}/${feedbackItems.length}`,
                                    })}
                                </Text>
                                <IconButton
                                    aria-label={'view next attempt'}
                                    disabled={currentFeedbackItemIndex === feedbackItems.length - 1}
                                    kind={'chevronRight'}
                                    size={'small'}
                                    variant={'square'}
                                    onClick={onClickNextAttempt}
                                />
                            </Stack>
                            <Dropdown
                                id={'instant-feedback-dropdown'}
                                options={[
                                    // { key: 'reset', value: 'Reset this attempt', icon: 'refresh' },
                                    {
                                        key: 'copy',
                                        value: t('grading-section.actions.copy-all-feedback', 'Copy all feedback'),
                                        icon: 'copy',
                                    },
                                    {
                                        key: 'delete',
                                        value: t('grading-section.actions.delete-all-feedback', 'Delete all feedback'),
                                        icon: 'delete',
                                    },
                                ]}
                                rootComponent={
                                    <IconButton
                                        aria-label={'more options'}
                                        kind={'moreHorizontal'}
                                        size={'small'}
                                        variant={'square'}
                                    />
                                }
                                onSelect={onSelect}
                            />
                        </Stack>
                    </Stack>
                    {currentFeedbackItem.is_failed || typeof form.feedback === 'string' ? (
                        <LegacyFeedbackRenderer
                            currentSubmission={currentFeedbackItem}
                            form={form}
                            setForm={(func) => {
                                setForm(func);
                                updateFeedback(func(form));
                            }}
                            setShowFailedDialog={null}
                        />
                    ) : (
                        <FeedbackRenderer
                            currentFeedbackItem={currentFeedbackItem}
                            form={form}
                            setForm={(func) => {
                                setForm(func);
                                updateFeedback(func(form));
                            }}
                            selectedFeedbackId={selectedFeedbackId}
                            setSelectedFeedbackId={setSelectedFeedbackId}
                        />
                    )}
                </RightStack>
            </Stack>
        </RootContainer>
    );
};

const RootContainer = styled(Container)(() => {
    return {
        position: 'absolute',
        top: 0,
        zIndex: theme.zIndex.drawer + 1,
        minHeight: '100%',
        padding: 0,
        paddingTop: '81px',
        background: theme.palette.ensightful.neutral[100],
    };
});

const LeftStack = styled(Stack)(({ isDocumentFullScreen }) => {
    return {
        position: 'relative',
        width: isDocumentFullScreen ? '100%' : '60%',
        height: 'calc(100vh - 81px)',
        transition: 'width 150ms',
        ...(!isDocumentFullScreen && {
            borderRight: `1px solid ${theme.palette.ensightful.neutral[700]}`,
        }),
    };
});

const BannerBox = styled(Box)(({ theme }) => {
    return {
        width: '100%',
        padding: theme.spacing(1, 5),
        background: theme.palette.ensightful.warning[200],
        color: theme.palette.ensightful.warning[900],
    };
});

const CustomRichTextArea = styled(RichTextArea)(() => {
    return {
        height: '100%',
        '& > .MuiRichTextArea-root': {
            minHeight: '100%',
            maxHeight: '100%',
        },
    };
});

const RightStack = styled(Stack)(({ theme, isDocumentFullScreen }) => {
    return {
        width: isDocumentFullScreen ? 0 : '40%',
        height: 'calc(100vh - 81px)',
        background: theme.palette.ensightful.shades[0],
        overflow: 'auto',
        transition: 'width 150ms',
    };
});

export default Desktop;
